import { supabase } from '@/lib/supabase'
import type { OpportunityItem } from '../types'
import { getCurrentPlanForUser } from '@/modules/plans/services/memberPlanService'
import { getLimitsFromPlan } from '@/modules/plans/services/opportunityLimits'
import { cycleWindow } from '@/modules/plans/utils/quota'
import type { OpportunityCycle, TimeframeKey } from '@/modules/plans/types'
import { timeframeForItem } from '@/modules/plans/utils/quota'

async function getAccountId(userId: string): Promise<string | null> {
  const { data, error } = await supabase.from('account_users').select('account_id').eq('user_id', userId).maybeSingle()
  if (error) throw error
  return (data as any)?.account_id || null
}

type ApplyQuotaResult = {
  items: OpportunityItem[]
  exceeded: boolean
  nextResetAt: string | null
}

export async function applyQuotaToItems(baseItems: OpportunityItem[]): Promise<ApplyQuotaResult> {
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData?.user?.id
  if (!userId) return { items: baseItems, exceeded: false, nextResetAt: null }

  // Plan & limits
  const { plan } = await getCurrentPlanForUser(userId)
  const limits = getLimitsFromPlan(plan || undefined)
  if (!plan || limits.mode === 'unlimited') return { items: baseItems, exceeded: false, nextResetAt: null }

  const accountId = await getAccountId(userId)
  if (!accountId) return { items: baseItems, exceeded: false, nextResetAt: null }

  // Window
  const window = cycleWindow(limits.active_cycle as OpportunityCycle, new Date())
  const cycle_start = window.start.toISOString()
  const cycle_end = window.end.toISOString()

  // Already seen in this window
  const { data: seenRows, error: seenErr } = await supabase
    .from('user_opportunity_visibilities')
    .select('opportunity_id,timeframe')
    .eq('user_id', userId)
    .eq('cycle', limits.active_cycle)
    .eq('cycle_start', cycle_start)
  if (seenErr) throw seenErr
  const seen = new Set<string>((seenRows || []).map((r: any) => String(r.opportunity_id)))
  const consumedByTf = new Map<TimeframeKey, number>()
  for (const r of seenRows || []) {
    const tf = String((r as any).timeframe || '') as TimeframeKey
    if (!tf) continue
    consumedByTf.set(tf, (consumedByTf.get(tf) || 0) + 1)
  }

  const itemsWithTf = baseItems.map((it) => ({ it, tf: timeframeForItem(it) }))
  const alwaysInclude = itemsWithTf.filter(({ it }) => seen.has(String(it.id)))

  // Compute remaining
  const total = Math.max(0, Math.floor(Number(limits.total || 0)))
  const consumedTotal = (seenRows || []).length
  let totalRemain = Math.max(0, total - consumedTotal)
  const alloc: Partial<Record<TimeframeKey, number>> = (limits.allocations || {})
  const remainByTf = new Map<TimeframeKey, number>()
  ;(Object.keys(alloc) as (keyof typeof alloc)[]).forEach((k) => {
    const tf = String(k) as TimeframeKey
    const max = Math.max(0, Math.floor(Number((alloc as any)[tf] || 0)))
    const used = consumedByTf.get(tf) || 0
    remainByTf.set(tf, Math.max(0, max - used))
  })

  const selected = new Set<string>(alwaysInclude.map(({ it }) => String(it.id)))
  const toInsert: Array<{ opportunity_id: string; timeframe: TimeframeKey }> = []

  for (const { it, tf } of itemsWithTf) {
    const id = String(it.id)
    if (selected.has(id)) continue
    if (!tf) continue
    const tfRemain = remainByTf.get(tf) || 0
    if (tfRemain <= 0 || totalRemain <= 0) continue
    selected.add(id)
    remainByTf.set(tf, tfRemain - 1)
    totalRemain -= 1
    toInsert.push({ opportunity_id: id, timeframe: tf })
  }

  // Persist newly visible (idempotente por unique constraint no banco)
  if (toInsert.length > 0) {
    const rows = toInsert.map((r) => ({
      id: crypto.randomUUID(),
      user_id: userId,
      account_id: accountId,
      plan_id: plan.id,
      cycle: limits.active_cycle,
      cycle_start,
      cycle_end,
      timeframe: r.timeframe,
      opportunity_id: r.opportunity_id,
      created_at: new Date().toISOString(),
    }))
    // Do in chunks to avoid size limits
    const chunkSize = 50
    for (let i = 0; i < rows.length; i += chunkSize) {
      const slice = rows.slice(i, i + chunkSize)
      const { error } = await supabase.from('user_opportunity_visibilities').insert(slice)
      if (error && !String(error.message || '').includes('duplicate key')) throw error
    }
  }

  const allowedItems = baseItems.filter((it) => selected.has(String(it.id)))
  const exceeded = baseItems.length > allowedItems.length
  return { items: allowedItems, exceeded, nextResetAt: cycle_end }
}
